/**
 * Habit Profile Sheet — full-screen detail view for a single habit.
 *
 * Layout (max-w-lg centered on desktop):
 *   - Header: back arrow + habit name
 *   - Hero: emoji + name + category + detail badges
 *   - Streak cards (3 across)
 *   - 1-year heatmap (proper month grouping, weekday labels, desktop-fit)
 *   - Stats grid
 *   - Created date
 */

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, Flame, Clock4, Hourglass, Target, Camera, Video, FileText,
  CalendarCheck, CalendarX, TrendingUp, Hash, Timer, Award,
} from 'lucide-react'
import {
  format, getISODay, eachDayOfInterval,
  startOfMonth, endOfMonth, subMonths,
} from 'date-fns'
import { uz } from 'date-fns/locale'
import { useHabitProfileStats } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'

/* ── Types ── */

interface YearlyLog { date: string; completed: boolean; value: number | null }

interface HabitProfileData {
  habit: {
    id: string; name: string; icon: string; category: string | null
    frequency: string; weekly_days: number[] | null; custom_interval: number | null
    start_date: string; end_date: string | null
    time_mode: string | null; time_duration: number | null; time_start: string | null
    value_required: boolean; value_target: number | null; value_unit: string | null
    proof_types: string[]; created_at: string
  }
  streak: { current: number; longest: number; start_date: string | null; last_completed: string | null }
  stats: {
    total_completed: number; total_missed: number; total_value: number
    applicable_days: number; total_days: number; completion_rate: number
  }
  yearly_logs: YearlyLog[]
}

/* ── Category names ── */

const CAT_UZ: Record<string, string> = {
  sport: 'Sport', education: "Ta'lim", spiritual: 'Ruhiy', business: 'Biznes',
  health: "Sog'liq", creativity: 'Ijod', mental: 'Aqliy', tech: 'IT',
  language: "Til o'rganish", lifestyle: 'Turmush tarzi',
}
const CAT_EN: Record<string, string> = {
  sport: 'Sport', education: 'Education', spiritual: 'Spiritual', business: 'Business',
  health: 'Health', creativity: 'Creativity', mental: 'Mental', tech: 'Technology',
  language: 'Language', lifestyle: 'Lifestyle',
}

/* ── Heatmap sizing ── */
const CELL = 11
const GAP = 2
const STEP = CELL + GAP
const DAY_LABEL_W = 20 // left weekday labels
const MONTH_GAP = 6   // gap between months

/* ═══ MAIN ═══ */

export const HabitProfileSheet: React.FC<{ habitId: string | null; onClose: () => void }> = ({ habitId, onClose }) => {
  const { t, i18n } = useTranslation()
  const { data: raw, isLoading } = useHabitProfileStats(habitId)
  const lang = i18n.language
  const locale = lang === 'uz' ? uz : undefined
  const profileData = raw as HabitProfileData | null

  if (!habitId) return null

  return (
    <AnimatePresence>
      <motion.div
        key="habit-profile"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 220 }}
        className="fixed inset-0 z-50 bg-[var(--color-bg)] overflow-y-auto pt-14"
      >
        {/* Header — 56px from top to avoid Telegram fullscreen buttons */}
        <div className="fixed top-14 left-0 right-0 z-10 bg-[var(--color-bg)]/90 backdrop-blur-sm border-b border-white/[0.06]">
          <div className="max-w-[1000px] mx-auto flex items-center gap-3 px-4 py-3">
            <button onClick={onClose} className="p-2 -ml-2 rounded-xl hover:bg-white/[0.04] transition-colors tap-highlight-transparent">
              <ArrowLeft size={20} className="text-text" />
            </button>
            <span className="text-[15px] font-display font-bold text-text truncate">
              {profileData?.habit.name ?? t('common.loading')}
            </span>
          </div>
        </div>

        {/* Content offset: 56px (top safe) + ~52px (header height) */}
        <div className="max-w-[1000px] mx-auto pt-14">
          {isLoading || !profileData ? (
            <div className="p-5 flex flex-col gap-4">
              <Skeleton className="h-24 w-full rounded-2xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
              <Skeleton className="h-32 w-full rounded-2xl" />
            </div>
          ) : (
            <ProfileContent data={profileData} lang={lang} locale={locale} t={t} />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

/* ── Content ── */

const ProfileContent: React.FC<{
  data: HabitProfileData; lang: string; locale: typeof uz | undefined
  t: (key: string, opts?: Record<string, unknown>) => string
}> = ({ data, lang, locale, t }) => {
  const { habit, streak, stats, yearly_logs } = data
  const catName = lang === 'uz' ? (CAT_UZ[habit.category ?? ''] ?? habit.category) : (CAT_EN[habit.category ?? ''] ?? habit.category)

  return (
    <div className="px-4 pb-24 flex flex-col gap-4 pt-4">
      {/* Hero */}
      <div className="flex flex-col items-center gap-2 py-4">
        <div className="w-20 h-20 rounded-3xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-4xl">
          {habit.icon}
        </div>
        <h2 className="text-xl font-display font-bold text-text text-center">{habit.name}</h2>
        {catName && (
          <span className="text-xs font-medium px-3 py-1 rounded-full bg-white/[0.06] text-text-hint">{catName}</span>
        )}
        <div className="flex items-center gap-2 flex-wrap justify-center mt-1">
          {habit.time_start && <DetailBadge icon={Clock4} color="var(--color-primary)" label={habit.time_start.slice(0, 5)} />}
          {habit.time_duration != null && habit.time_duration > 0 && (
            <DetailBadge icon={Hourglass} color="var(--color-warning)" label={`${habit.time_duration} ${lang === 'uz' ? 'daq' : 'min'}`} />
          )}
          {habit.value_required && habit.value_target && (
            <DetailBadge icon={Target} color="var(--color-success)" label={`${habit.value_target}${habit.value_unit ? ` ${habit.value_unit}` : ''}`} />
          )}
          {habit.proof_types.length > 0 && (
            <DetailBadge icon={habit.proof_types.includes('image') ? Camera : habit.proof_types.includes('video') ? Video : FileText} color="var(--color-accent)" label={lang === 'uz' ? 'Dalil' : 'Proof'} />
          )}
        </div>
      </div>

      {/* Streak row */}
      <div className="flex gap-2.5">
        <TopStat icon={Flame} color="#F97316" value={streak.current} label={t('home.habitProfile.currentStreak')} />
        <TopStat icon={Award} color="#22C55E" value={streak.longest} label={t('home.habitProfile.longestStreak')} />
        <TopStat icon={TrendingUp} color="#3B82F6" value={`${stats.completion_rate}%`} label={t('home.habitProfile.rate')} />
      </div>

      {/* Heatmap */}
      <div className="rounded-2xl glass p-4">
        <h3 className="text-[13px] font-display font-bold text-text mb-3">{t('home.habitProfile.yearlyActivity')}</h3>
        <YearHeatmap
          logs={yearly_logs}
          locale={locale}
          lang={lang}
          habitInfo={{
            frequency: habit.frequency,
            weekly_days: habit.weekly_days,
            custom_interval: habit.custom_interval,
            start_date: habit.start_date,
            end_date: habit.end_date,
            value_required: habit.value_required,
          }}
        />
      </div>

      {/* Stats */}
      <div className="rounded-2xl glass p-4">
        <h3 className="text-[13px] font-display font-bold text-text mb-3">{t('home.habitProfile.statistics')}</h3>
        <div className="grid grid-cols-2 gap-2.5">
          <StatCell icon={CalendarCheck} color="#22C55E" value={stats.total_completed} label={t('home.habitProfile.completed')} />
          <StatCell icon={CalendarX} color="#EF4444" value={stats.total_missed} label={t('home.habitProfile.missed')} />
          <StatCell icon={Hash} color="#3B82F6" value={stats.applicable_days} label={t('home.habitProfile.applicableDays')} />
          {stats.total_value > 0 && (
            <StatCell icon={Target} color="#14B8A6" value={`${stats.total_value}${habit.value_unit ? ` ${habit.value_unit}` : ''}`} label={t('home.habitProfile.totalValue')} />
          )}
          {habit.time_duration != null && habit.time_duration > 0 && (
            <StatCell icon={Timer} color="#F59E0B" value={`${stats.total_completed * habit.time_duration} ${lang === 'uz' ? 'daq' : 'min'}`} label={t('home.habitProfile.totalTime')} />
          )}
        </div>
      </div>

      <div className="text-center text-[11px] text-text-hint/40 pb-4">
        {t('home.habitProfile.createdAt', { date: format(new Date(habit.created_at), 'd MMMM yyyy', { locale }) })}
      </div>
    </div>
  )
}

/* ── Small components ── */

const DetailBadge: React.FC<{ icon: React.ElementType; color: string; label: string }> = ({ icon: Icon, color, label }) => (
  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.04] border border-white/[0.06]">
    <Icon size={11} style={{ color }} fill="currentColor" />
    <span className="text-[10px] text-text-hint">{label}</span>
  </div>
)

const TopStat: React.FC<{ icon: React.ElementType; color: string; value: string | number; label: string }> = ({ icon: Icon, color, value, label }) => (
  <div className="flex-1 rounded-xl glass p-3 flex flex-col items-center gap-1">
    <Icon size={18} style={{ color }} />
    <span className="text-lg font-display font-bold text-text tabular-nums">{value}</span>
    <span className="text-[9px] text-text-hint text-center leading-tight">{label}</span>
  </div>
)

const StatCell: React.FC<{ icon: React.ElementType; color: string; value: string | number; label: string }> = ({ icon: Icon, color, value, label }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3 flex items-center gap-2.5">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
      <Icon size={15} style={{ color }} />
    </div>
    <div className="flex flex-col min-w-0">
      <span className="text-[15px] font-display font-bold text-text tabular-nums truncate">{value}</span>
      <span className="text-[10px] text-text-hint truncate">{label}</span>
    </div>
  </div>
)

/* ═══ YEAR HEATMAP ═══
 *
 * 5 intensity levels (like profile heatmap):
 *   - Value-based habits: value/maxValue ratio → 5 rang
 *   - Simple habits: completed = full, missed = empty
 *   - Non-applicable days: distinct dim pattern (not "missed")
 *
 * Layout: 12 months (sliding window → today), ISO weeks (Mon start)
 * Gap between months, weekday labels left, month labels top, tooltip
 */

const WEEKDAYS_UZ = ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya']
const WEEKDAYS_EN = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

const HEAT_COLORS = [
  'rgba(255,255,255,0.04)',  // 0: no data / non-applicable
  'rgba(34,197,94,0.2)',     // 1: low
  'rgba(34,197,94,0.4)',     // 2: medium-low
  'rgba(34,197,94,0.65)',    // 3: medium-high
  'rgba(34,197,94,0.9)',     // 4: high / full
] as const

const NOT_APPLICABLE_FILL = 'rgba(255,255,255,0.015)' // dimmer than empty — clearly "doesn't apply"

interface MonthBlock {
  label: string
  weeks: (Date | null)[][] // 7 rows × N cols, null = empty cell
}

interface HeatmapHabitInfo {
  frequency: string
  weekly_days: number[] | null
  custom_interval: number | null
  start_date: string
  end_date: string | null
  value_required: boolean
}

const YearHeatmap: React.FC<{
  logs: YearlyLog[]
  locale: typeof uz | undefined
  lang: string
  habitInfo?: HeatmapHabitInfo
}> = ({ logs, locale, lang, habitInfo }) => {
  const { t } = useTranslation()

  const { logMap, valueMap, maxValue, monthBlocks, totalW, totalH } = useMemo(() => {
    // Log lookups
    const lm = new Map<string, boolean>()
    const vm = new Map<string, number>()
    let mv = 0
    for (const log of logs) {
      if (log.completed) {
        lm.set(log.date, true)
        if (log.value != null && log.value > 0) {
          vm.set(log.date, log.value)
          if (log.value > mv) mv = log.value
        }
      }
    }

    // Generate 12 months ending with current month
    const today = new Date()
    const blocks: MonthBlock[] = []

    for (let i = 11; i >= 0; i--) {
      const refDate = subMonths(today, i)
      const mStart = startOfMonth(refDate)
      const mEnd = i === 0 ? today : endOfMonth(refDate)
      const allDays = eachDayOfInterval({ start: mStart, end: mEnd })

      const cols: (Date | null)[][] = []
      let currentCol: (Date | null)[] = new Array(7).fill(null) as (Date | null)[]

      for (const day of allDays) {
        const row = getISODay(day) - 1
        if (row === 0 && currentCol.some((d) => d !== null)) {
          cols.push(currentCol)
          currentCol = new Array(7).fill(null) as (Date | null)[]
        }
        currentCol[row] = day
      }
      if (currentCol.some((d) => d !== null)) {
        cols.push(currentCol)
      }

      blocks.push({ label: format(mStart, 'LLL', { locale }), weeks: cols })
    }

    const weekdayLabelW = DAY_LABEL_W
    let tw = weekdayLabelW
    for (const block of blocks) {
      tw += block.weeks.length * STEP + MONTH_GAP
    }
    const th = 16 + 7 * STEP

    return { logMap: lm, valueMap: vm, maxValue: mv, monthBlocks: blocks, totalW: tw, totalH: th }
  }, [logs, locale])

  const isValueBased = habitInfo?.value_required === true && maxValue > 0

  function getCellFill(dateStr: string, day: Date): string {
    // Check if habit applies on this day (frequency-aware)
    if (habitInfo) {
      const freq = habitInfo.frequency
      if (freq === 'weekly_days' && habitInfo.weekly_days) {
        const isoDay = getISODay(day)
        if (!habitInfo.weekly_days.includes(isoDay)) return NOT_APPLICABLE_FILL
      } else if (freq === 'custom_interval' && habitInfo.custom_interval && habitInfo.start_date) {
        const startMs = new Date(habitInfo.start_date).getTime()
        const checkMs = day.getTime()
        const diffDays = Math.round((checkMs - startMs) / 86_400_000)
        if (diffDays < 0 || diffDays % habitInfo.custom_interval !== 0) return NOT_APPLICABLE_FILL
      }
      if (habitInfo.start_date && dateStr < habitInfo.start_date) return NOT_APPLICABLE_FILL
      if (habitInfo.end_date && dateStr > habitInfo.end_date) return NOT_APPLICABLE_FILL
    }

    const completed = logMap.get(dateStr)
    if (!completed) return HEAT_COLORS[0]

    // Value-based: intensity by value/maxValue
    if (isValueBased) {
      const val = valueMap.get(dateStr) ?? 0
      if (val <= 0) return HEAT_COLORS[1]
      const ratio = val / maxValue
      if (ratio <= 0.25) return HEAT_COLORS[1]
      if (ratio <= 0.5) return HEAT_COLORS[2]
      if (ratio <= 0.75) return HEAT_COLORS[3]
      return HEAT_COLORS[4]
    }

    // Simple habit: completed = full green
    return HEAT_COLORS[4]
  }

  function getTooltip(dateStr: string): string {
    const completed = logMap.get(dateStr)
    if (!completed) return dateStr
    const val = valueMap.get(dateStr)
    if (val != null && val > 0) return `${dateStr}: ${val}`
    return `${dateStr}: ✓`
  }

  const weekdays = lang === 'uz' ? WEEKDAYS_UZ : WEEKDAYS_EN

  return (
    <div>
      <div className="overflow-x-auto flex justify-center scrollbar-hide">
        <svg width={totalW} height={totalH} style={{ display: 'block', minWidth: totalW, flexShrink: 0 }}>
          {weekdays.map((wd, i) => (
            <text key={`wd-${i}`} x={0} y={16 + i * STEP + CELL / 2 + 3} fill="#768c9e" fontSize={8} fontWeight={500}>
              {wd}
            </text>
          ))}

          {(() => {
            let offsetX = DAY_LABEL_W
            return monthBlocks.map((block, mi) => {
              const blockX = offsetX
              offsetX += block.weeks.length * STEP + MONTH_GAP

              return (
                <g key={`mb-${mi}`}>
                  <text x={blockX} y={11} fill="#768c9e" fontSize={9} fontWeight={600}>
                    {block.label}
                  </text>

                  {block.weeks.map((col, colIdx) =>
                    col.map((day, row) => {
                      if (!day) return null
                      const ds = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
                      return (
                        <rect
                          key={ds}
                          x={blockX + colIdx * STEP}
                          y={16 + row * STEP}
                          width={CELL}
                          height={CELL}
                          rx={2}
                          fill={getCellFill(ds, day)}
                        >
                          <title>{getTooltip(ds)}</title>
                        </rect>
                      )
                    })
                  )}
                </g>
              )
            })
          })()}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-1 mt-2 text-[10px] text-text-hint">
        <span>{t('home.habitProfile.less')}</span>
        {HEAT_COLORS.map((color, i) => (
          <div key={i} className="w-[10px] h-[10px] rounded-sm" style={{ backgroundColor: color }} />
        ))}
        <span>{t('home.habitProfile.more')}</span>
      </div>
    </div>
  )
}
